import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  Contractor,
  UpdateContractorDto,
  ContractorDocument
} from '@umss/shared/data-access';

/**
 * Service for managing contractor profile data
 * Uses /api/contractors/me endpoints for current contractor operations
 * BE handles camelCase ↔ PascalCase conversion automatically
 */
@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private readonly apiUrl = '/api/Contractors';

  constructor(private http: HttpClient) {}

  /**
   * Get current contractor's profile
   * @returns Observable of Contractor
   */
  getProfile(): Observable<Contractor> {
    return this.http.get<Contractor>(`${this.apiUrl}/me`);
  }

  /**
   * Update current contractor's profile
   * @param profile - Partial UpdateContractorDto with fields to update
   * @returns Observable of updated Contractor
   */
  updateProfile(profile: Partial<UpdateContractorDto>): Observable<Contractor> {
    return this.http.put<Contractor>(`${this.apiUrl}/me`, profile);
  }

  /**
   * Get contractor documents (certifications, licenses, etc.)
   * @param contractorId - Contractor ID
   * @returns Observable of ContractorDocument array
   */
  getDocuments(contractorId: string): Observable<ContractorDocument[]> {
    return this.http.get<ContractorDocument[]>(
      `${this.apiUrl}/${contractorId}/documents`
    );
  }

  /**
   * Upload a contractor document (certification or document)
   * Uses /me/documents endpoint for current contractor
   * @param file - File to upload
   * @param documentType - Type of document (from DocumentType enum)
   * @param expirationDate - Optional expiration date (YYYY-MM-DD format)
   * @returns Observable of created ContractorDocument
   */
  uploadDocument(
    file: File,
    documentType: string,
    expirationDate?: string
  ): Observable<ContractorDocument> {
    return new Observable((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1]; // Remove data:image/png;base64, prefix

        const payload = {
          documentType,
          fileName: file.name,
          fileContent: base64String,
          mimeType: file.type,
          expirationDate: expirationDate || null
        };

        this.http
          .post<ContractorDocument>(`${this.apiUrl}/me/documents`, payload)
          .subscribe({
            next: (doc) => observer.next(doc),
            error: (err) => observer.error(err),
            complete: () => observer.complete()
          });
      };
      reader.onerror = () => observer.error(new Error('Failed to read file'));
      reader.readAsDataURL(file); // Changed from readAsArrayBuffer to readAsDataURL
    });
  }

  /**
   * Delete a contractor document
   * @param documentId - Document ID to delete
   * @returns Observable<void>
   */
  deleteDocument(documentId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/documents/${documentId}`);
  }

  /**
   * Upload profile picture for current contractor
   * @param file - Image file to upload (JPEG, PNG, WEBP)
   * @returns Observable of updated Contractor
   */
  uploadProfilePicture(file: File): Observable<Contractor> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<Contractor>(
      `${this.apiUrl}/me/profile-picture`,
      formData
    );
  }

  /**
   * Delete profile picture for current contractor
   * @returns Observable<void>
   */
  deleteProfilePicture(): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/me/profile-picture`);
  }
}
